import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import FormInstanceTabState from 'forms/form-instance/FormInstanceTabState';
import {
  FormInstanceFileStateQuery,
  FormInstanceFileStateQueryVariables,
} from 'forms/form-instance/graphql/__generated/FormInstanceFileStateQuery';
import {FORM_INSTANCE_FILE_STATE_QUERY} from 'forms/form-instance/graphql/GetFormDefinition';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';

type FormDefinition = FormInstanceFileStateQuery['formDefinition'];

interface FormInstanceFileStateAttributes {
  formDefinition?: FormDefinition;
  name?: string;
  instanceUrl?: string;
}

class FormInstanceFileState extends ApolloFileState<
  FormInstanceFileStateQuery,
  FormInstanceFileStateQueryVariables,
  FormInstanceFileStateAttributes
> {
  static fileType = ModelTypeConstants.FORM_DEFINITION;
  static getQueryOptions(fileId: string): QueryOptions<FormInstanceFileStateQueryVariables> {
    return {
      query: FORM_INSTANCE_FILE_STATE_QUERY,
      variables: {
        formDefinitionId: fileId,
      },
    };
  }

  static dataToAttributes(data: FormInstanceFileStateQuery): FormInstanceFileStateAttributes {
    const {formDefinition} = data;
    return isInaccessible(formDefinition)
      ? {}
      : {
          formDefinition,
          name: formDefinition.name,
          instanceUrl: formDefinition.instanceUrl,
        };
  }

  static openFile(fileId: string): JQueryPromise<FormInstanceFileState> {
    return super.openFile(fileId) as JQueryPromise<FormInstanceFileState>;
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }
  initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers()
      .at(0)
      .tabs()
      .add(
        new FormInstanceTabState({
          formDefinitionId: this.id,
        })
      );

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  editURL(): string | undefined {
    return this.get('instanceUrl');
  }

  name(): string {
    return this.get('name') ?? 'Loading...';
  }

  registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }
}

export default FormInstanceFileState;
